// Banner shown when a Service Worker update is pending.
//
// Two states with different urgency:
//
//   - waiting (soft): a new SW is installed but the current bundle
//     is still wire-compatible. User can finish what they're doing
//     and click "Reload to update" when they're ready.
//
//   - protocolSkew (hard): the active SW reports a PROTOCOL_VERSION
//     this bundle wasn't built against. Any further envelope traffic
//     is unsafe — the SW will reject or rewrite frames whose shape
//     this bundle expects. We surface the same banner but the button
//     is non-dismissable and the copy makes the reason explicit. The
//     reload still happens through the same handshake path
//     (skipWaiting → activated → reload), and falls back to a direct
//     `location.reload()` when there is no waiting SW to activate
//     (skew detected against an already-controlling SW).

import { useEffect, useState, type ReactElement } from 'react';
import { activateWaitingServiceWorker, onRegistrationChange } from './register';

export function UpdateBanner(): ReactElement | null {
  const [waiting, setWaiting] = useState(false);
  const [skew, setSkew] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(
    () =>
      onRegistrationChange((s) => {
        setWaiting(s.waiting);
        setSkew(s.protocolSkew);
      }),
    [],
  );

  // Reload the page once the new SW activates. The SW broadcasts
  // 'sw.activated' which `onRegistrationChange` translates to
  // `waiting=false, active=true`; reloading at that point ensures the
  // user starts on the new bundle.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    const onMessage = (ev: MessageEvent) => {
      const d = ev.data as { type?: string };
      if (d && d.type === 'sw.activated' && pending) {
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [pending]);

  if (!waiting && !skew) return null;

  const isSkew = skew;
  const label = isSkew
    ? 'This tab is out of date. Reload to keep working.'
    : 'A new version is available.';
  const buttonLabel = pending ? 'Reloading…' : isSkew ? 'Reload now' : 'Reload to update';

  return (
    <div
      role="status"
      data-testid="sw-update-banner"
      data-variant={isSkew ? 'skew' : 'waiting'}
      className={
        'fixed inset-x-0 bottom-3 z-50 mx-auto w-fit max-w-md rounded-md border px-4 py-2 text-sm shadow-md ' +
        (isSkew
          ? 'border-destructive/60 bg-destructive/10 text-destructive'
          : 'border-primary/40 bg-card')
      }
    >
      <span>{label}</span>{' '}
      <button
        type="button"
        onClick={async () => {
          setPending(true);
          if (isSkew) {
            // Skew can occur against an already-controlling SW (no
            // `waiting` SW to flip). Always reload from the page even
            // if there's nothing to skipWaiting on — the SW will
            // re-handshake on the next boot and clear the flag.
            await activateWaitingServiceWorker();
            window.location.reload();
            return;
          }
          await activateWaitingServiceWorker();
        }}
        className={
          'ml-2 rounded px-2 py-0.5 text-xs font-semibold ' +
          (isSkew
            ? 'bg-destructive text-destructive-foreground hover:opacity-90'
            : 'bg-primary text-primary-foreground hover:opacity-90') +
          ' disabled:opacity-60'
        }
        disabled={pending}
        data-testid="sw-update-reload"
      >
        {buttonLabel}
      </button>
    </div>
  );
}
